'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import { useAuth } from '@/contexts/AuthContext';

import { openCalendly } from '@/lib/ctaRouting';
import { getUserContext } from '@/lib/userContext';
import { trackDiscoveryCallClick } from '@/lib/analytics';

export default function PricingPageClient() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    setMounted(true);
  }, []);

  const createCheckoutSessionWithTimeout = async (planId: string, attempt: number = 1): Promise<{ success: boolean; sessionUrl?: string; error?: string }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId }),
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const checkoutData = await response.json();

      if (response.ok && checkoutData.sessionUrl) {
        return { success: true, sessionUrl: checkoutData.sessionUrl };
      } else {
        return { success: false, error: checkoutData.error || 'Failed to create checkout session' };
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      
      if (err.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection. Please check your network and try again.' };
      }
      
      return { success: false, error: 'Network error occurred. Please try again.' };
    }
  };

  const handleStartNow = async (planName: string) => {
    // Map plan names to plan IDs
    const planIdMap: Record<string, string> = {
      'Sirloin Cut': 'sirloin',
      'Ribeye Cut': 'ribeye',
      'Tomahawk Cut': 'tomahawk'
    };
    const planId = planIdMap[planName];

    if (!planId) {
      console.error('Invalid plan name:', planName);
      setCheckoutError('Invalid plan selected. Please try again.');
      return;
    }

    // Check if user is authenticated
    if (isAuthenticated) {
      // User is authenticated - create checkout session directly with retry logic
      setCheckoutLoading(planName);
      setCheckoutError(null);
      setRetryCount(0);
      
      let attempt = 1;
      
      while (attempt <= MAX_RETRIES) {
        const result = await createCheckoutSessionWithTimeout(planId, attempt);
        
        if (result.success && result.sessionUrl) {
          // Redirect to Stripe checkout
          window.location.href = result.sessionUrl;
          return;
        }
        
        // If failed and we have retries left
        if (attempt < MAX_RETRIES) {
          setRetryCount(attempt);
          setCheckoutError(`Checkout failed (attempt ${attempt}/${MAX_RETRIES}). Retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5s before retry
          attempt++;
        } else {
          // All retries exhausted
          console.error('Checkout session creation failed after retries:', result.error);
          setCheckoutError(result.error || 'Failed to create checkout session after multiple attempts. Please try again later.');
          setCheckoutLoading(null);
          return;
        }
      }
    } else {
      // User is not authenticated - redirect to login with plan parameter
      router?.push(`/login?plan=${encodeURIComponent(planName)}`);
    }
  };

  const handleBookCall = () => {
    getUserContext()?.then(context => {
      const segment = context?.role || 'unauthenticated';
      trackDiscoveryCallClick(
        segment === 'super_admin' ? 'super_admin' : 
        segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
        segment === 'team' ? 'team' : 
        segment === 'client' ? 'client' : 'unauthenticated',
        '/pricing-plans-subscription-details',
        'pricing_card'
      );
    });
    openCalendly();
  };

  const pricingPlans = [
    {
      id: 1,
      name: 'Sirloin Cut',
      price: '$1,600',
      description: 'For teams that need steady creative production',
      features: [
        'One active request at a time',
        'Predictable weekly output',
        'Design, video, and web updates',
        'Email support and async video feedback',
        'Unlimited revisions on active request',
        'Pause or cancel anytime'
      ]
    },
    {
      id: 2,
      name: 'Ribeye Cut',
      price: '$2,800',
      description: 'For teams managing multiple priorities and campaigns',
      features: [
        'Two active requests simultaneously',
        'Parallel workstreams',
        'Monthly strategy huddle',
        'Priority support',
        'Unlimited revisions on all requests',
        'Pause or cancel anytime'
      ],
      isPopular: true
    },
    {
      id: 3,
      name: 'Tomahawk Cut',
      price: '$3,900',
      description: 'For organizations wanting a full creative department without hiring one',
      features: [
        'Three active requests simultaneously',
        'Dedicated creative team',
        'Weekly strategy sessions',
        'White-glove priority support',
        'Unlimited revisions on all requests',
        'Pause or cancel anytime'
      ]
    }
  ];

  if (!mounted) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="w-full bg-gradient-to-b from-[#3f1651] to-[#2a0f3a] py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
            <div className="text-center">
              <h1 className="text-[40px] sm:text-[60px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-white mb-4 sm:mb-6">
                Choose Your Cut
              </h1>
              <p className="text-[16px] sm:text-[18px] lg:text-[20px] font-poppins font-normal leading-[1.6] text-[#e4e4e4] max-w-3xl mx-auto">
                All plans include unlimited requests, unlimited revisions, and pause or cancel anytime. No contracts, no surprises.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards Section */}
        <section className="w-full bg-[#f5f3f7] py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                    plan.isPopular ? 'ring-4 ring-[#8923b3] transform scale-105' : ''
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#8923b3] text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="text-center mb-6 sm:mb-8 pt-4">
                    <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                      {plan.name}
                    </h3>
                    <div className="mb-4">
                      <span className="text-[40px] sm:text-[48px] lg:text-[56px] font-bebas font-normal text-[#8923b3]">
                        {plan.price}
                      </span>
                      <span className="text-[16px] sm:text-[18px] font-poppins font-normal text-[#666666]">/month</span>
                    </div>
                    <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#666666] leading-[1.5] mb-4">
                      {plan.description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#8923b3] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    {checkoutError && checkoutLoading === plan.name && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">
                        {checkoutError}
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleStartNow(plan.name)}
                      disabled={checkoutLoading === plan.name}
                      className={`w-full py-3 sm:py-4 rounded-lg font-poppins font-semibold text-[14px] sm:text-[16px] transition-all duration-300 ${
                        plan.isPopular
                          ? 'bg-[#8923b3] text-white hover:bg-[#6d1c8f] shadow-lg hover:shadow-xl'
                          : 'bg-[#3f1651] text-white hover:bg-[#2a0f3a]'
                      } ${checkoutLoading === plan.name ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {checkoutLoading === plan.name ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {retryCount > 0 ? `Retrying (${retryCount}/${MAX_RETRIES})...` : 'Processing...'}
                        </span>
                      ) : (
                        'Start Now'
                      )}
                    </button>
                    <button
                      onClick={handleBookCall}
                      className="block w-full py-4 rounded-full text-sm font-medium uppercase tracking-wider transition-all duration-300 text-center bg-transparent text-[#8923b3] border-2 border-[#8923b3] hover:bg-[#f5f3f7]"
                    >
                      Book a discovery call
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Unlimited Means Section */}
        <section className="w-full bg-white py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-20">
            <h2 className="text-[32px] sm:text-[48px] lg:text-[64px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651] mb-8 sm:mb-12">
              What "Unlimited" Really Means
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <div className="w-12 h-12 bg-[#8923b3] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="text-[20px] sm:text-[24px] font-bebas font-normal text-[#3f1651] uppercase mb-3">
                  Unlimited Requests
                </h3>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                  Add as many requests to your queue as you need. We work through them based on your plan's active request limit, completing them one (or more) at a time.
                </p>
              </div>

              <div className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <div className="w-12 h-12 bg-[#8923b3] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-[20px] sm:text-[24px] font-bebas font-normal text-[#3f1651] uppercase mb-3">
                  Unlimited Revisions
                </h3>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                  Not happy with a deliverable? Request revisions until it's exactly what you need. No extra charges, no revision limits.
                </p>
              </div>

              <div className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <div className="w-12 h-12 bg-[#8923b3] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-[20px] sm:text-[24px] font-bebas font-normal text-[#3f1651] uppercase mb-3">
                  Pause or Cancel Anytime
                </h3>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                  Need to pause your subscription? No problem. Pause when you don't need us, resume when you do. Cancel anytime with no penalties.
                </p>
              </div>

              <div className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <div className="w-12 h-12 bg-[#8923b3] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-[20px] sm:text-[24px] font-bebas font-normal text-[#3f1651] uppercase mb-3">
                  Dedicated Team
                </h3>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                  Your subscription includes access to designers, video editors, web developers, and social media specialists—all working on your projects.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Happens After Signup Section */}
        <section className="w-full bg-[#f5f3f7] py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-20">
            <h2 className="text-[32px] sm:text-[48px] lg:text-[64px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651] mb-8 sm:mb-12">
              What Happens After You Sign Up
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#8923b3] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bebas text-xl">1</span>
                  </div>
                  <div>
                    <h3 className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                      Onboarding Call (Within 24 Hours)
                    </h3>
                    <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                      We schedule a quick call to understand your brand, goals, and immediate priorities. We'll also collect brand assets and access to any tools you use.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#8923b3] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bebas text-xl">2</span>
                  </div>
                  <div>
                    <h3 className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                      Submit Your First Requests
                    </h3>
                    <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                      Add tasks to your queue through our client portal. Include as much detail as you'd like—links, examples, brand guidelines, or just a quick description.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#8923b3] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bebas text-xl">3</span>
                  </div>
                  <div>
                    <h3 className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                      We Start Working
                    </h3>
                    <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                      Your dedicated team begins working on your active requests. You'll receive updates and drafts for review, typically within 24-72 hours depending on complexity.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#8923b3] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bebas text-xl">4</span>
                  </div>
                  <div>
                    <h3 className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                      Review, Revise, Approve
                    </h3>
                    <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                      Review deliverables in your portal. Request revisions if needed (unlimited!), or approve and move on to the next task. It's that simple.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full bg-white py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-20">
            <h2 className="text-[32px] sm:text-[48px] lg:text-[64px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651] mb-8 sm:mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <details className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <summary className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase cursor-pointer">
                  How does the unlimited model work?
                </summary>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6] mt-4">
                  You can add as many requests as you need to your queue. We work through them based on your plan's active request limit. For example, if you have the Ribeye plan (2 active requests), we'll work on 2 tasks simultaneously while others wait in queue.
                </p>
              </details>

              <details className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <summary className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase cursor-pointer">
                  What's the average turnaround time?
                </summary>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6] mt-4">
                  Most requests are completed within 24-72 hours, depending on complexity. Simple graphics might be done in a day, while more complex video editing or web development could take a few days.
                </p>
              </details>

              <details className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <summary className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase cursor-pointer">
                  Can I pause my subscription?
                </summary>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6] mt-4">
                  Yes! You can pause your subscription at any time. When paused, your billing stops and you can resume whenever you're ready. This is perfect for seasonal businesses or when you have a lighter workload.
                </p>
              </details>

              <details className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8">
                <summary className="text-[18px] sm:text-[20px] font-bebas font-normal text-[#3f1651] uppercase cursor-pointer">
                  What if I don't like the work?
                </summary>
                <p className="text-[14px] sm:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6] mt-4">
                  We offer unlimited revisions on all deliverables. If something isn't quite right, just let us know what needs to change and we'll revise it until you're 100% satisfied.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="w-full bg-gradient-to-b from-[#3f1651] to-[#2a0f3a] py-12 sm:py-16 lg:py-20">
          <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-20 text-center">
            <h2 className="text-[32px] sm:text-[48px] lg:text-[64px] font-bebas font-normal leading-[1.1] text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-[16px] sm:text-[18px] font-poppins font-normal text-[#e4e4e4] leading-[1.6] mb-8 max-w-2xl mx-auto">
              Choose your plan and start getting consistent, high-quality creative work delivered every week.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-10 py-4 bg-[#8923b3] text-white text-sm font-medium uppercase tracking-wider rounded-full hover:bg-[#a035d1] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Start Now
              </button>
              <button
                onClick={handleBookCall}
                className="px-10 py-4 bg-transparent text-white text-sm font-medium uppercase tracking-wider rounded-full border-2 border-white hover:bg-white hover:text-[#3f1651] transition-all duration-300"
              >
                Book a Call
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}